// ============================================================================
// OBT F3-D — Kurumsal Hafıza ekranı (park varlık geçmişi)
// ============================================================================
// İŞ EMRİ / BAKIM / ONAY AKIŞI DEĞİLDİR — yalnız uzun yıllar saklanacak kurumsal
// varlık kayıtları. Kapsam backend'de (JWT + ChainService) çözülür; ekran hiçbir
// aktör/scope göndermez. Düzenleme yalnız yazma-yetkili rollerde; garanti durumu
// backend'de TÜRETİLİR (sahte flag yok).
// ============================================================================
import { useMemo, useState } from 'react';
import { ArrowLeft, Archive, Boxes, History, ShieldCheck, Image, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  ObtPageHeader,
  ObtLoading,
  ObtErrorState,
  ObtEmptyState,
  ObtStatTile,
} from '../ObtStates';
import { normalizeObtError } from '../obtErrors';
import { createAsset, addAssetRecord } from './memoryApi';
import { useScopeParks } from './hooks/useScopeParks';
import { useParkAssetHistory } from './hooks/useParkAssetHistory';
import AssetHistoryTree from './AssetHistoryTree';
import AssetRecordCard from './AssetRecordCard';
import AddAssetDialog from './AddAssetDialog';
import AddRecordDialog from './AddRecordDialog';
import { AssetTypeBadge, WarrantyBadge } from './MemoryBadges';
import { ASSET_TYPE_LABELS, WARRANTY_STATUS_LABELS, memoryLabel } from './memoryEnums';
import { formatDate } from '../report/reportFormat';

const TABS = [
  { key: 'assets', label: 'Varlıklar', icon: Boxes },
  { key: 'history', label: 'Geçmiş', icon: History },
  { key: 'warranty', label: 'Garanti', icon: ShieldCheck },
  { key: 'photos', label: 'Fotoğraflar', icon: Image },
];

const EDIT_ROLES = ['director', 'deputy', 'engineer'];

export default function ObtMemoryScreen({ role = 'director', enabled = true, onBack }) {
  const canEdit = EDIT_ROLES.includes(role);
  const scope = useScopeParks({ enabled });
  const [parkId, setParkId] = useState('');
  const hist = useParkAssetHistory(parkId, { enabled: enabled && Boolean(parkId) });
  const [tab, setTab] = useState('assets');
  const [assetOpen, setAssetOpen] = useState(false);
  const [recordFor, setRecordFor] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [pending, setPending] = useState(false);

  const assets = hist.history?.assets || [];

  const run = async (fn) => {
    if (pending) return;
    setPending(true);
    setActionError(null);
    try {
      await fn();
      await hist.reload();
      await scope.reload();
    } catch (error) {
      setActionError(normalizeObtError(error));
      throw error;
    } finally {
      setPending(false);
    }
  };

  const allRecords = useMemo(
    () =>
      assets
        .flatMap(({ asset, records }) =>
          (records || []).map((r) => ({ ...r, _assetName: asset.name || asset.asset_type }))
        )
        .sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || ''))),
    [assets]
  );
  const photoRecords = useMemo(
    () => allRecords.filter((r) => Array.isArray(r.attachments) && r.attachments.length > 0),
    [allRecords]
  );

  return (
    <div className="space-y-4">
      <ObtPageHeader
        onBack={onBack}
        title="Kurumsal Hafıza"
        description="Park varlıklarının (havuz, saha, aydınlatma…) yapım/değişim geçmişi, firma, ihale ve garanti kayıtları."
      />

      {/* Park seçici */}
      <Card>
        <CardContent className="space-y-2 p-4">
          <Label htmlFor="memory-park">Park</Label>
          {scope.loading ? (
            <ObtLoading label="Parklar yükleniyor…" />
          ) : scope.error ? (
            <ObtErrorState error={scope.error} onRetry={scope.reload} />
          ) : scope.parks.length === 0 ? (
            <ObtEmptyState title="Kapsamınızda park yok" description="Kurumsal hafıza için erişebileceğiniz bir park bulunmuyor." />
          ) : (
            <select
              id="memory-park"
              className="h-9 w-full rounded-md border border-gray-300 bg-white px-2 text-sm"
              value={parkId}
              onChange={(e) => setParkId(e.target.value)}
            >
              <option value="">Bir park seçin…</option>
              {scope.parks.map((p) => (
                <option key={String(p.park_id)} value={p.park_id}>
                  {p.park_name}
                  {typeof p.asset_count === 'number' ? ` (${p.asset_count} varlık)` : ''}
                </option>
              ))}
            </select>
          )}
        </CardContent>
      </Card>

      {!parkId && (
        <ObtEmptyState title="Park seçilmedi" description="Varlık geçmişini görmek için bir park seçin." icon={Archive} />
      )}

      {parkId && hist.loading && <ObtLoading label="Varlık geçmişi yükleniyor…" />}
      {parkId && !hist.loading && hist.error && <ObtErrorState error={hist.error} onRetry={hist.reload} />}

      {parkId && !hist.loading && !hist.error && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div role="tablist" aria-label="Kurumsal hafıza sekmeleri" className="flex flex-wrap gap-1.5">
              {TABS.map((t) => {
                const Icon = t.icon;
                const active = tab === t.key;
                return (
                  <button
                    key={t.key}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setTab(t.key)}
                    className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium ring-1 ring-inset ${
                      active ? 'bg-blue-600 text-white ring-blue-600' : 'bg-white text-gray-700 ring-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="h-4 w-4" aria-hidden="true" /> {t.label}
                  </button>
                );
              })}
            </div>
            {canEdit && (
              <Button size="sm" onClick={() => setAssetOpen(true)} disabled={pending}>
                <Plus className="mr-2 h-4 w-4" aria-hidden="true" /> Varlık Ekle
              </Button>
            )}
          </div>

          {actionError && <p role="alert" className="text-sm text-red-600">{actionError.message}</p>}

          <div role="tabpanel">
            {tab === 'assets' && (
              <AssetsTab assets={assets} canEdit={canEdit} onAddRecord={setRecordFor} />
            )}
            {tab === 'history' && (
              <AssetHistoryTree
                assets={assets}
                canEdit={canEdit}
                onAddRecord={setRecordFor}
                showPhotos
              />
            )}
            {tab === 'warranty' && <WarrantyTab assets={assets} />}
            {tab === 'photos' && <PhotosTab photoRecords={photoRecords} />}
          </div>
        </>
      )}

      <AddAssetDialog
        open={assetOpen}
        error={actionError?.message || null}
        onClose={() => setAssetOpen(false)}
        onCreate={(payload) => run(() => createAsset(parkId, payload))}
      />
      <AddRecordDialog
        open={Boolean(recordFor)}
        asset={recordFor}
        error={actionError?.message || null}
        onClose={() => setRecordFor(null)}
        onAdd={({ fields, files }) => run(() => addAssetRecord(recordFor.asset_id, fields, files))}
      />
    </div>
  );
}

function AssetsTab({ assets, canEdit, onAddRecord }) {
  if (assets.length === 0) {
    return <ObtEmptyState title="Bu parkta kayıtlı varlık yok" description="Yeni bir varlık ekleyin." icon={Boxes} />;
  }
  return (
    <div className="space-y-2">
      {assets.map(({ asset }) => (
        <Card key={String(asset.asset_id)}>
          <CardContent className="flex flex-col gap-2 p-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="min-w-0 space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold text-gray-900">
                  {asset.name || memoryLabel(ASSET_TYPE_LABELS, asset.asset_type)}
                </span>
                <AssetTypeBadge type={asset.asset_type} />
                <WarrantyBadge status={asset.warranty_status} />
              </div>
              <div className="flex flex-wrap gap-x-4 text-xs text-gray-500">
                <span>{asset.record_count} kayıt</span>
                <span>{asset.replacement_count} değişim</span>
                {asset.last_event_date && <span>Son olay: {formatDate(asset.last_event_date)}</span>}
                {asset.last_company_name && <span>Son firma: {asset.last_company_name}</span>}
              </div>
            </div>
            {canEdit && (
              <Button size="sm" variant="outline" onClick={() => onAddRecord(asset)}>
                <Plus className="mr-1 h-4 w-4" aria-hidden="true" /> Kayıt Ekle
              </Button>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function WarrantyTab({ assets }) {
  const withWarranty = assets.filter(({ asset }) => asset.warranty_status !== 'unknown');
  const active = withWarranty.filter(({ asset }) => asset.warranty_status === 'active');
  const expired = withWarranty.filter(({ asset }) => asset.warranty_status === 'expired');
  const unknown = assets.filter(({ asset }) => asset.warranty_status === 'unknown');

  if (assets.length === 0) {
    return <ObtEmptyState title="Varlık yok" />;
  }
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-3">
        <ObtStatTile label={WARRANTY_STATUS_LABELS.active} value={active.length} icon={ShieldCheck} tone="success" />
        <ObtStatTile label={WARRANTY_STATUS_LABELS.expired} value={expired.length} tone="danger" />
        <ObtStatTile label={WARRANTY_STATUS_LABELS.unknown} value={unknown.length} tone="neutral" />
      </div>
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs text-gray-500">
            <tr>
              <th className="px-3 py-2">Varlık</th>
              <th className="px-3 py-2">Tip</th>
              <th className="px-3 py-2">Garanti durumu</th>
              <th className="px-3 py-2">Bitiş</th>
            </tr>
          </thead>
          <tbody>
            {assets.map(({ asset }) => (
              <tr key={String(asset.asset_id)} className="border-t border-gray-100">
                <td className="px-3 py-2 font-medium text-gray-900">
                  {asset.name || memoryLabel(ASSET_TYPE_LABELS, asset.asset_type)}
                </td>
                <td className="px-3 py-2">{memoryLabel(ASSET_TYPE_LABELS, asset.asset_type)}</td>
                <td className="px-3 py-2"><WarrantyBadge status={asset.warranty_status} /></td>
                <td className="px-3 py-2 text-gray-600">{formatDate(asset.warranty_end_date)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PhotosTab({ photoRecords }) {
  if (photoRecords.length === 0) {
    return <ObtEmptyState title="Fotoğraf yok" description="Kurumsal hafıza kayıtlarına bağlı fotoğraf bulunmuyor." icon={Image} />;
  }
  return (
    <div className="space-y-3">
      {photoRecords.map((r) => (
        <div key={String(r.record_id)} className="space-y-1">
          <p className="text-xs font-medium text-gray-600">{r._assetName}</p>
          <AssetRecordCard record={r} showPhotos />
        </div>
      ))}
    </div>
  );
}
